import { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { SERIES_COLORS } from "./palette";

export interface ShareSegment { key: string; label: string; value: number; color?: string }
export type StackedShareBarProps = {
  segments: ShareSegment[];
  ariaLabel: string;
  /** Valor do segmento no tooltip (padrão: número pt-BR). */
  formatValue?: (value: number) => string;
};

/** Piso do rótulo: abaixo disso o % nunca aparece no segmento (vale também quando a largura da barra é desconhecida). */
export const MIN_LABEL_PCT = 8;
/** Largura mínima legível do rótulo: "100%" em 12 px seminegrito mede ~27 px. Segmento mais estreito fica só com o tooltip. */
export const MIN_LABEL_PX = 28;

const defaultFormat = (v: number) => v.toLocaleString("pt-BR");

const lum = (hex: string) => {
  const n = parseInt(hex.replace("#", "").slice(0, 6), 16);
  const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => v / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};
/** Branco quando dá >= 4.5:1 sobre o fundo; senão o marinho (ciano e âmbar do SERIES_COLORS falham com branco). */
export const shareTextColor = (bg: string) => (/^#[0-9a-f]{6}$/i.test(bg) && 1.05 / (lum(bg) + 0.05) < 4.5 ? "#17384c" : "#fff");

/** Largura atual da barra em px (null enquanto não medida, p. ex. no jsdom). */
function useWidth() {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

// "Mix por canal" (final-g1-visao-geral.png): 66% | 29% | 5%. A cor segue a posição original (estável por canal). MUI puro.
// O % só aparece se cabe no segmento (MIN_LABEL_PCT e MIN_LABEL_PX); todo segmento tem tooltip (hover, foco e toque) com nome, % e valor.
export function StackedShareBar({ segments, ariaLabel, formatValue = defaultFormat }: StackedShareBarProps) {
  const [ref, width] = useWidth();
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0) || 1;
  return (
    <Box ref={ref} role="group" aria-label={ariaLabel} sx={{ display: "flex", height: "28px", borderRadius: "4px", overflow: "hidden" }}>
      {segments.map((s, i) => {
        if (s.value <= 0) return null;
        const pct = Math.round((s.value / total) * 100);
        const bg = s.color ?? SERIES_COLORS[i % SERIES_COLORS.length];
        const fits = pct >= MIN_LABEL_PCT && (width === null || (width * s.value) / total >= MIN_LABEL_PX);
        const text = `${s.label}: ${pct}% · ${formatValue(s.value)}`;
        // Só o segmento com o % escondido vira parada de Tab (o visível já lê o rótulo); minWidth 2px: valor > 0 nunca some.
        return (
          <Tooltip key={s.key} title={text} describeChild arrow enterTouchDelay={0}>
            <Box data-testid={`share-${s.key}`} role="img" aria-label={text} tabIndex={fits ? undefined : 0}
              sx={{ width: `${pct}%`, minWidth: "2px", overflow: "hidden", whiteSpace: "nowrap", bgcolor: bg, color: shareTextColor(bg), fontSize: "12px", fontWeight: 600, display: "flex", alignItems: "center", justifyContent: "center", "&:focus-visible": { outline: "2px solid #17384c", outlineOffset: "-2px" } }}>
              {fits ? `${pct}%` : ""}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}
